import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        organisation: '',
        enquiry_type: '',
        message: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (value) => {
        setFormData(prev => ({ ...prev, enquiry_type: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await axios.post(`${API_URL}/contact/submit`, formData);
            setIsSubmitted(true);
            toast.success('Enquiry submitted successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center">
                    <CardContent className="pt-8 pb-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                        <p className="text-gray-600 mb-6">
                            Your enquiry has been received. We'll get back to you as soon as possible.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button onClick={() => navigate('/')} variant="outline">
                                Back to Home
                            </Button>
                            <Button onClick={() => {
                                setIsSubmitted(false);
                                setFormData({
                                    name: '',
                                    email: '',
                                    phone: '',
                                    organisation: '',
                                    enquiry_type: '',
                                    message: ''
                                });
                            }}>
                                Submit Another Enquiry
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-[#1a3a2f] border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => navigate('/')}
                                className="text-white hover:bg-white/10"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <img 
                                src="/react-qld-logo.png" 
                                alt="REACT QLD" 
                                className="h-8 object-contain cursor-pointer"
                                onClick={() => navigate('/')}
                            />
                        </div>
                        <Button 
                            onClick={() => navigate('/login')}
                            className="bg-primary hover:bg-primary/90 text-white"
                        >
                            Member Login
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-3xl mx-auto px-4 py-12">
                <div className="text-center mb-8">
                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                        Get in Touch
                    </h1>
                    <p className="text-lg text-gray-600">
                        Interested in REACT QLD for your organisation? Fill out the form below and we'll be in touch.
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Contact Form</CardTitle>
                        <CardDescription>
                            All fields marked with * are required
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name *</Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        placeholder="John Smith"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                        data-testid="contact-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address *</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="john@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                        data-testid="contact-email"
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        placeholder="0412 345 678"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                        data-testid="contact-phone"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="organisation">Organisation *</Label>
                                    <Input
                                        id="organisation"
                                        name="organisation"
                                        placeholder="Your Organisation"
                                        value={formData.organisation}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                        data-testid="contact-organisation"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="enquiry_type">Enquiry Type *</Label>
                                <Select 
                                    value={formData.enquiry_type} 
                                    onValueChange={handleSelectChange}
                                    disabled={isSubmitting}
                                    required
                                >
                                    <SelectTrigger data-testid="contact-enquiry-type">
                                        <SelectValue placeholder="Select enquiry type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="demo">Request a Demo</SelectItem>
                                        <SelectItem value="pricing">Pricing Information</SelectItem>
                                        <SelectItem value="features">Feature Questions</SelectItem>
                                        <SelectItem value="support">Technical Support</SelectItem>
                                        <SelectItem value="partnership">Partnership Enquiry</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message">Message *</Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    placeholder="Tell us about your organisation and how we can help..."
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    className="min-h-[150px]"
                                    data-testid="contact-message"
                                />
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                                <p>
                                    By submitting this form, you agree to be contacted by REACT QLD regarding your enquiry. 
                                    Your information will be handled in accordance with our privacy policy.
                                </p>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full h-12 text-lg"
                                disabled={isSubmitting || !formData.enquiry_type}
                                data-testid="contact-submit"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-5 w-5" />
                                        Send Enquiry
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-gray-500 text-sm mt-6">
                    You can also email us directly at{' '}
                    <a href="mailto:help@reactqld.com" className="text-primary hover:underline">
                        help@reactqld.com
                    </a>
                </p>
            </main>
        </div>
    );
}
